import React from 'react';
import {Row, Col, Text, Icon, toaster, showConfirm} from '@mc/ui-v4';
import {useDropzone} from 'react-dropzone';
import {FieldRenderProps} from 'react-final-form';
import {mcNew} from '@mc/react-ui-icons';
import './LayerFileDropzone.scss';
import {LayerFileTable} from './LayerFileTable';
import {FeatureCollection} from 'geojson';
import {randomInt} from '../../../helpers/LayerConfigUtils';

export type LayerDropzoneValue = {
    id: string;
    name: string;
    sourceFileName: string;
    data: FeatureCollection;
};

export type LayerFileDropzoneProps = {
    onDocumentUpload: (document: File) => Promise<LayerDropzoneValue[]>;
    onFocus?: (val?: LayerDropzoneValue[]) => void;
    onBlur?: (val?: LayerDropzoneValue[]) => void;
    onChange?: (val?: LayerDropzoneValue[]) => void;
    disabled?: boolean;
    readOnly?: boolean;
    value?: LayerDropzoneValue[];
    fileObjects?: LayerDropzoneValue[];
    setFileObjects?: React.Dispatch<React.SetStateAction<LayerDropzoneValue[]>>;
    multiple?: boolean;
    maxFiles?: number;
} & FieldRenderProps<LayerDropzoneValue[]>;

export const LayerFileDropzone: React.FC<LayerFileDropzoneProps> = ({
    onDocumentUpload,
    onChange,
    value,
    readOnly = false,
    disabled = false,
    multiple = false,
    maxFiles = 0,
    fileObjects,
    setFileObjects,
}) => {
    const readFile = (file: File): Promise<LayerDropzoneValue[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onabort = () => toaster.error('Čtení souboru bylo přerušeno.');
            reader.onerror = () => toaster.error('Při čtení souboru došlo k chybě.');
            reader.onload = () => {
                onDocumentUpload(file)
                    .then(data => {
                        const addedFileObjects = [];
                        data.forEach(newFileObject => {
                            const existingFileNames = fileObjects.map(obj => obj.sourceFileName);
                            if (!existingFileNames.includes(newFileObject.sourceFileName)) {
                                setFileObjects(prevFileObjects => [...prevFileObjects, newFileObject]);
                                addedFileObjects.push(newFileObject); // Přidání do pole pro vyřešení
                            } else {
                                toaster.warning('Tento soubor již byl přidán a nebude znovu přidán.');
                            }
                        });

                        if (addedFileObjects.length > 0) {
                            resolve(addedFileObjects); // Vracíme pole nových objektů souborů
                        } else {
                            toaster.warning('Žádný nový soubor nebyl přidán.');
                        }
                    })
                    .catch(err => {
                        toaster.error(err.messageDesc);
                        reject(err);
                    });
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const handleOnDrop = async (files: File[]) => {
        const datas = [...fileObjects];
        for (let i = 0; i < files.length; i++) {
            const readFiles = await readFile(files[i]);
            datas.push(...readFiles);
        }
        onChange && onChange(datas);
    };

    const {getRootProps, getInputProps} = useDropzone({
        // accept: acceptedTypes,
        multiple: multiple,
        maxFiles: maxFiles,
        preventDropOnDocument: true,
        onDrop: handleOnDrop,
        onDropRejected: () => toaster.warning('Nepovolený formát souboru'),
    });

    const handleDelete = async (e: React.MouseEvent<HTMLElement, MouseEvent>, fileId: string) => {
        console.log('<<< handleDelete', fileId);
        e.stopPropagation();
        const result = await showConfirm({
            title: 'Odstranit dokument',
            message: 'Opravdu chcete odstranit dokument?',
            intent: 'danger',
            confirmText: 'Odstranit',
        });
        if (result) {
            const filteredFileObjects = fileObjects.filter(x => x.id !== fileId);
            setFileObjects(filteredFileObjects);
            onChange(value.filter(x => x.id !== fileId));
        }
    };

    let minHeight = fileObjects.length > 0 ? `${28 * fileObjects.length + 34}px` : '';

    return (
        <div className={'layerfile-dropzone'}>
            <Row>
                <Col>
                    {readOnly || disabled || (
                        <div {...getRootProps({className: 'dropzone'})}>
                            <input {...getInputProps()} id="layer-file-dropzone" />
                            <Text intent={'primary'} className={'my-auto'}>
                                <Icon icon={mcNew} className={'mr-2'} />
                                {'Přetáhněte soubor pro nahrání nebo seznam povolených formátů včetně DGN'}
                            </Text>
                        </div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col style={{minHeight: minHeight}}>
                    {fileObjects && fileObjects.length > 0 && <LayerFileTable files={fileObjects} handleDelete={handleDelete} />}
                </Col>
            </Row>
        </div>
    );
};
